import React from "react";
import { Platform } from "react-native";

import { Circle, G, Line } from "react-native-svg";

import { BOARD_BG } from "../../theme/boardColors";
import type { PlacedOption } from "./boardLayout";
import type { GhostOption } from "./optionMotion";
import {
  OPTION_FILL,
  OPTION_FILL_HOVER,
  OPTION_HIT_R,
  OPTION_OUTLINE,
  SPOKE_COLOR,
  SPOKE_WIDTH,
} from "./options";
import { strokeWidthProps } from "./strokes";

/** Same casing rule as every other link: #121212 at width + 3.5. */
const SPOKE_CASING = SPOKE_WIDTH + 3.5;
const OUTLINE_WIDTH = 1.5;
const RING_GAP = 3;
const RING_WIDTH = 2;

type Pos = { px: number; py: number };
type Ghost = GhostOption & { opacity: number };

const spoke = (a: Pos, b: Pos, t: number, upp: number, key: string) => {
  const g = {
    x1: a.px,
    y1: a.py,
    x2: a.px + (b.px - a.px) * t,
    y2: a.py + (b.py - a.py) * t,
    strokeLinecap: "round" as const,
  };
  return (
    <G key={key}>
      <Line {...g} stroke={BOARD_BG} {...strokeWidthProps(SPOKE_CASING, upp)} />
      <Line
        {...g}
        stroke={SPOKE_COLOR}
        {...strokeWidthProps(SPOKE_WIDTH, upp)}
      />
    </G>
  );
};

/** Quiet spokes from the current word to each option (under the path). */
export const BoardSpokes: React.FC<{
  current: Pos | undefined;
  options: PlacedOption[];
  ghosts: Ghost[];
  scaleOf: (id: string) => number;
  unitsPerPx: number;
}> = ({ current, options, ghosts, scaleOf, unitsPerPx }) => (
  <G>
    {ghosts.map(
      (g) =>
        g.from && (
          <G key={`ghost-${g.id}`} opacity={g.opacity}>
            {spoke(g.from, g, 1, unitsPerPx, g.id)}
          </G>
        ),
    )}
    {current &&
      options.map((o) => {
        const s = scaleOf(o.id);
        return s > 0
          ? spoke(current, o, Math.min(1, s), unitsPerPx, `spoke-${o.id}`)
          : null;
      })}
  </G>
);

interface OptionNodesProps {
  options: PlacedOption[];
  ghosts: Ghost[];
  scaleOf: (id: string) => number;
  hovered: string | null;
  hint: string | null;
  hintColor: string;
  unitsPerPx: number;
  onPress: (id: string) => void;
  onHover: (id: string | null) => void;
}

/**
 * The options as small muted candidate nodes, tappable like the word buttons.
 * An option already on the board (a path word or the target) gets only its
 * hint or hover ring here; BoardNodes draws the node itself.
 */
export const BoardOptionNodes: React.FC<OptionNodesProps> = ({
  options,
  ghosts,
  scaleOf,
  hovered,
  hint,
  hintColor,
  unitsPerPx,
  onPress,
  onHover,
}) => {
  const outline = {
    stroke: OPTION_OUTLINE,
    ...strokeWidthProps(OUTLINE_WIDTH, unitsPerPx),
  };
  const ring = (r: number, color: string) => (
    <Circle
      r={r + RING_GAP}
      fill="none"
      stroke={color}
      {...strokeWidthProps(RING_WIDTH, unitsPerPx)}
    />
  );
  return (
    <G>
      {ghosts.map((g) => (
        <G
          key={`ghost-${g.id}`}
          transform={`translate(${g.px},${g.py})`}
          opacity={g.opacity}
        >
          <Circle r={g.r} fill={OPTION_FILL} {...outline} />
        </G>
      ))}
      {options.map((o) => {
        const s = scaleOf(o.id);
        if (s <= 0) return null;
        const press = () => onPress(o.id);
        const handlers =
          Platform.OS === "web"
            ? {
                onClick: press,
                onMouseEnter: () => onHover(o.id),
                onMouseLeave: () => onHover(null),
              }
            : { onPress: press };
        const isHint = hint === o.id;
        const isHover = hovered === o.id;
        if (o.existing) {
          return isHint || isHover ? (
            <G key={o.id} transform={`translate(${o.px},${o.py})`}>
              {ring(o.r + 1, isHint ? hintColor : OPTION_FILL_HOVER)}
            </G>
          ) : null;
        }
        return (
          <G
            key={o.id}
            transform={`translate(${o.px},${o.py})`}
            testID={`board-option-${o.id}`}
          >
            <G transform={s !== 1 ? `scale(${s})` : undefined}>
              <Circle r={OPTION_HIT_R} fill="transparent" {...handlers} />
              {isHint && ring(o.r, hintColor)}
              <Circle
                r={o.r}
                fill={isHover ? OPTION_FILL_HOVER : OPTION_FILL}
                {...outline}
                {...handlers}
              />
            </G>
          </G>
        );
      })}
    </G>
  );
};
